
import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { getTranslatedProductName, getTranslatedCategory } from '../../utils/translationHelpers';
import { 
  User, 
  MapPin, 
  Package, 
  ShoppingCart, 
  Search, 
  Filter, 
  Star,
  CheckCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Carrot
} from 'lucide-react';
import {
  OptimizedFormContainer,
  OptimizedFormSection,
  OptimizedFormGrid,
  OptimizedProductGrid,
  OptimizedProductCard,
  OptimizedOrderSummary,
  OptimizedSubmitButton,
  SearchAndFilterContainer,
  SearchInput,
  FilterSelect,
  CollapsibleSection,
  CategoryBadge,
  SectionTitle,
  FieldContainer,
  QuantityControl,
  LoadingSpinner,
  GlobalOrderSummary,
  ErrorMessage
} from './shared/OptimizedFormComponents';
import {
  CustomerInformationSection,
  OrderInformationSection,
  SharedOrderFormData,
  getMockCustomerData
} from './shared/SharedFormSections';
import { useCustomerData } from './shared/CustomerDataService';
import { useGlobalOrderData } from '../../hooks/useGlobalOrderData';
import { FormStatusIndicator } from './shared/FormStatusIndicator';
import { PreviewOrderButton } from './shared/PreviewOrderButton';
import { BackToTopButton } from './shared/BackToTopButton';
import { ThemeColors, Spacing, Typography } from '../../styles/ThemeStyles';
import { productToFormItem, Product } from '../../services/ProductService';
import { useProducts } from '../../hooks/useProducts';

interface OrderItem {
  id: string;
  packetDescription: string;
  code: string;
  quantity: number;
  price: number;
  category: 'vegetable';
}

interface OrderFormData extends SharedOrderFormData {
  items: OrderItem[];
}

// Products will be loaded from Firestore

// Memoized Product Card Component to prevent unnecessary re-renders
interface ProductCardProps {
  productCode: string;
  productName: string;
  productPrice: string;
  quantity: number;
  isSelected: boolean;
  category: string;
  onQuantityChange: (productId: string, quantity: number) => void;
  codeLabel: string;
}

const ProductCardMemo = React.memo<ProductCardProps>(({
  productCode,
  productName,
  productPrice,
  quantity,
  isSelected,
  category,
  onQuantityChange,
  codeLabel
}) => {
  const handleChange = useCallback((newQuantity: number) => {
    onQuantityChange(productCode, newQuantity);
  }, [productCode, onQuantityChange]);

  return (
    <OptimizedProductCard isSelected={isSelected}>
      <div className="product-header">
        <div className="product-title">
          {productName}
        </div>
        <div className="product-price">${productPrice}</div>
      </div>
      
      <div className="product-code">{codeLabel}: {productCode}</div>
      
      <CategoryBadge category={category} />
      
      <QuantityControl
        value={quantity}
        onChange={handleChange}
      />
    </OptimizedProductCard>
  );
}, (prevProps, nextProps) => {
  // Custom comparison function - only re-render if these props change
  return (
    prevProps.productCode === nextProps.productCode &&
    prevProps.quantity === nextProps.quantity &&
    prevProps.isSelected === nextProps.isSelected &&
    prevProps.productName === nextProps.productName &&
    prevProps.productPrice === nextProps.productPrice
  );
});

ProductCardMemo.displayName = 'ProductCardMemo';

const FormValidation = styled.div`
  margin-bottom: ${Spacing.lg};
`;

const ProductCategorySection = styled.div`
  margin-bottom: ${Spacing.xl};
`;

const CategoryHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${Spacing.lg};
  padding: ${Spacing.md} ${Spacing.lg};
  background: linear-gradient(135deg, ${ThemeColors.primary.light} 0%, ${ThemeColors.primary.light}dd 100%);
  border-radius: ${Spacing.md};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
  
  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
    transform: translateY(-1px);
  }
  
  .category-info {
    display: flex;
    align-items: center;
    gap: ${Spacing.md};
    flex: 1;
    
    .category-title {
      font-size: ${Typography.fontSize.lg};
      font-weight: ${Typography.fontWeight.bold};
      color: ${ThemeColors.text.primary};
      letter-spacing: -0.02em;
    }
    
    .category-count {
      background: ${ThemeColors.primary.main};
      color: white;
      padding: ${Spacing.xs} ${Spacing.md};
      border-radius: 20px;
      font-size: ${Typography.fontSize.sm};
      font-weight: ${Typography.fontWeight.semiBold};
      white-space: nowrap;
      box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    }
  }
  
  .category-price {
    font-size: ${Typography.fontSize.lg};
    font-weight: ${Typography.fontWeight.semiBold};
    color: ${ThemeColors.primary.main};
    white-space: nowrap;
    opacity: 0.9;
  }
`;

export const OptimizedVegetableOrderForm: React.FC<{ storeId?: string; onShowPreview?: () => void }> = ({ storeId, onShowPreview }) => {
  const { t, i18n } = useTranslation();
  
  // Fetch customer data from Firebase
  const { customerData, loading: customerLoading, error: customerError } = useCustomerData(storeId || '');
  const { 
    orderData, 
    updateOrderData, 
    updateVegetableItems, 
    submitOrder, 
    isLoading: globalLoading,
    getFormStatus,
    autoSave,
    clearDraft
  } = useGlobalOrderData();
  
  const [selectedItems, setSelectedItems] = useState<{ [key: string]: number }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [collapsedSections, setCollapsedSections] = useState<{ [key: string]: boolean }>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const prevItemsRef = useRef<string>('');
  const currentItemsRef = useRef<string>('');
  const isUserEditingRef = useRef<boolean>(false); // Track when user is actively editing
  const userEditTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Get form status for the status indicator
  const formStatus = getFormStatus('vegetable');

  // Load products from Firestore
  const { products, loading: productsLoading, error: productsError } = useProducts('Vegetable');
  
  // Reset initialization when storeId changes
  useEffect(() => {
    setIsInitialized(false);
    setSelectedItems({});
    prevItemsRef.current = '';
    currentItemsRef.current = '';
    isUserEditingRef.current = false; // Reset editing flag on store change
    if (userEditTimeoutRef.current) {
      clearTimeout(userEditTimeoutRef.current);
      userEditTimeoutRef.current = null;
    }
  }, [storeId]);

  // Reset local state when orderData is cleared (e.g., after clearDraft)
  // This ensures UI reflects the cleared state immediately
  useEffect(() => {
    // If orderData.vegetableItems is explicitly empty (length 0) and we have items locally,
    // it means the order was reset/cleared, so we should clear local state too
    if (orderData.vegetableItems !== undefined && 
        orderData.vegetableItems.length === 0) {
      const hasLocalItems = Object.keys(selectedItems).length > 0;
      if (hasLocalItems || currentItemsRef.current !== '') {
        console.log('🔄 Order data cleared - resetting local selectedItems state', {
          hadLocalItems: hasLocalItems,
          previousKey: currentItemsRef.current
        });
        setSelectedItems({});
        setIsInitialized(false);
        currentItemsRef.current = '';
        prevItemsRef.current = '';
        isUserEditingRef.current = false;
      }
    }
  }, [orderData.vegetableItems]); // Only depend on orderData.vegetableItems to avoid loops

  // Sync selected items with global order data (source of truth)
  // This works offline-first: loads from orderData immediately, works even if products haven't loaded
  // Re-syncs whenever orderData.vegetableItems changes or products load
  // BUT: Don't sync if user is actively editing (prevents overwriting user changes)
  useEffect(() => {
    // Wait for orderData to be available (not undefined)
    // Empty array [] is valid and means no items selected
    if (orderData.vegetableItems === undefined) {
      console.log('⏸️ Waiting for orderData.vegetableItems to load...');
      return;
    }
    
    // Don't sync if user is actively editing (prevents overwriting user changes)
    if (isUserEditingRef.current) {
      console.log('⏸️ Skipping sync - user is actively editing');
      return;
    }
    
    // Create a stable string representation for comparison
    const currentItemsKey = orderData.vegetableItems.length > 0
      ? orderData.vegetableItems
          .map(item => `${item.id}:${item.quantity}`)
          .sort()
          .join('|')
      : '';
    
    // Only process if items actually changed (prevents infinite loops)
    if (currentItemsRef.current === currentItemsKey) {
      return;
    }
    
    // Check if local selectedItems has more items than orderData
    // This indicates a user change in progress that hasn't been saved yet
    // BUT: Allow sync if local is empty (initial load) or if orderData has more items (data loaded from Firestore)
    const localItemCount = Object.values(selectedItems).reduce((sum, qty) => sum + qty, 0);
    const orderDataItemCount = orderData.vegetableItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
    
    const itemsLength = orderData.vegetableItems.length;
    
    // If orderData is empty, always clear local state (this handles reset/clearDraft cases)
    if (itemsLength === 0) {
      // Always clear selectedItems when orderData is empty, regardless of initialization state
      // This ensures that after clearDraft or reset, the UI reflects the cleared state
      if (Object.keys(selectedItems).length > 0 || currentItemsRef.current !== '') {
      setSelectedItems({});
        setIsInitialized(true);
        currentItemsRef.current = currentItemsKey;
      prevItemsRef.current = currentItemsKey;
        console.log('🔄 Vegetable form reset - items cleared (orderData is empty)');
      } else {
        // On initial load, if orderData is empty and local is already empty, just mark as processed
        currentItemsRef.current = currentItemsKey;
        prevItemsRef.current = currentItemsKey;
        if (!isInitialized) {
          setIsInitialized(true);
          console.log('✅ Initial load - orderData is empty, local state already cleared');
        }
      }
      return;
    }
    
    // Only block sync if local has items but orderData is empty AND we haven't initialized
    // This prevents overwriting user changes during initial load, but only if orderData hasn't loaded yet
    // NOTE: This check happens AFTER the empty check above, so it only applies when orderData has items
    if (localItemCount > 0 && orderDataItemCount === 0 && !isInitialized) {
      // This is a rare edge case: user has items locally but orderData is empty and we haven't initialized yet
      // This could happen if orderData hasn't finished loading from Firestore/cache yet
      console.log('⏸️ Local state has items but orderData is empty on initial load - waiting for data to load', {
        localCount: localItemCount,
        orderDataCount: orderDataItemCount
      });
      return;
    }
    
    // Build itemsMap from orderData.vegetableItems (source of truth)
    // This works offline-first: doesn't require products to be loaded
      const itemsMap: { [key: string]: number } = {};
    const missingProducts: string[] = [];
    
    orderData.vegetableItems.forEach((item, index) => {
      // Use item.id (which should be ItemCode) and quantity from orderData
      if (item.id && typeof item.quantity === 'number' && item.quantity > 0) {
        itemsMap[item.id] = item.quantity;
        
        // Verify the item exists in products (for validation, but don't block if offline)
        if (!productsLoading && products.length > 0) {
          const productExists = products.some(p => p.ItemCode === item.id);
          if (!productExists) {
            missingProducts.push(item.id);
          }
        }
      } else {
        console.warn(`⚠️ Skipping invalid item at index ${index}:`, item);
      }
    });
    
    // Log warnings for missing products (but still include them - offline-first approach)
    if (missingProducts.length > 0) {
      console.warn(`⚠️ ${missingProducts.length} product(s) not found in products list (might be offline or removed):`, missingProducts);
    }
    
    // Always update selectedItems to match orderData (source of truth)
    // This ensures UI reflects what's actually saved, even with bad network
      setSelectedItems(itemsMap);
      setIsInitialized(true);
    currentItemsRef.current = currentItemsKey;
      prevItemsRef.current = currentItemsKey;
    
    const totalQuantity = Object.values(itemsMap).reduce((sum, qty) => sum + qty, 0);
    console.log('✅ Vegetable form synced with orderData:', {
      itemsInOrderData: itemsLength,
      itemsMapped: Object.keys(itemsMap).length,
      totalQuantity: totalQuantity,
      itemIds: Object.keys(itemsMap),
      missingProducts: missingProducts.length > 0 ? missingProducts : 'none',
      isInitialLoad: !isInitialized
    });
  }, [orderData.vegetableItems, products, productsLoading, isInitialized]); // Include isInitialized to track initial load
  
  // Customer data sync is now handled by CustomerAndOrderSections component

  const handleInputChange = (field: keyof SharedOrderFormData, value: string | boolean) => {
    updateOrderData({ [field]: value });
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
    // Trigger auto-save
    autoSave();
  };

  // Debounce auto-save to prevent excessive Firestore writes
  const autoSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Use a ref to track the latest vegetableItems to avoid stale closures in rapid updates
  const vegetableItemsRef = useRef(orderData.vegetableItems || []);
  useEffect(() => {
    vegetableItemsRef.current = orderData.vegetableItems || [];
  }, [orderData.vegetableItems]);

  const handleQuantityChange = useCallback((productId: string, quantity: number) => {
    // Mark that user is actively editing
    isUserEditingRef.current = true;
    
    // Clear any existing timeout
    if (userEditTimeoutRef.current) {
      clearTimeout(userEditTimeoutRef.current);
    }
    
    // Update local state immediately for responsive UI
    setSelectedItems(prev => ({
      ...prev,
      [productId]: Math.max(0, quantity)
    }));
    
    // Update global vegetable items using ref to get latest state (prevents race conditions)
    // This ensures that when multiple products are selected quickly, each update works with the latest state
    const product = products.find(p => p.ItemCode === productId);
    if (product) {
      // Use ref to get the latest vegetableItems, not the stale closure value
      // This is critical when multiple products are selected in quick succession
      const currentItems = vegetableItemsRef.current;
      const formItem = productToFormItem(product, quantity);
      const updatedItems = quantity > 0 
        ? [...currentItems.filter(item => item.id !== productId), formItem]
        : currentItems.filter(item => item.id !== productId);
      
      // Update the ref immediately so subsequent rapid updates use the latest state
      vegetableItemsRef.current = updatedItems;
      
      // Update global state (this will trigger save)
      updateVegetableItems(updatedItems);
    }
    
    // Debounce auto-save to prevent excessive writes
    if (autoSaveTimeoutRef.current) {
      clearTimeout(autoSaveTimeoutRef.current);
    }
    autoSaveTimeoutRef.current = setTimeout(() => {
    autoSave();
    }, 1000); // Wait 1 second after last change before auto-saving
    
    // Allow sync to resume after user stops editing (wait 2 seconds after last change)
    userEditTimeoutRef.current = setTimeout(() => {
      isUserEditingRef.current = false;
      console.log('✅ User editing complete - sync enabled');
    }, 2000); // Wait 2 seconds after last change before allowing sync
  }, [products, updateVegetableItems, autoSave]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimeoutRef.current) {
        clearTimeout(autoSaveTimeoutRef.current);
      }
      if (userEditTimeoutRef.current) {
        clearTimeout(userEditTimeoutRef.current);
      }
    };
  }, []);


  const toggleSection = (sectionId: string) => {
    setCollapsedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!orderData.orderDate) {
      newErrors.orderDate = 'Order date is required';
    }
    if (!orderData.deliveryDate) {
      newErrors.deliveryDate = 'Delivery date is required';
    }

    // Check if at least one item is selected
    const hasItems = Object.values(selectedItems).some(qty => qty > 0);
    if (!hasItems) {
      newErrors.items = t('forms.selectAtLeastOneItem');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const calculateTotal = () => {
    return Object.entries(selectedItems).reduce((total, [productId, quantity]) => {
      const product = products.find(p => p.ItemCode === productId);
      return total + (product ? product.Price * quantity : 0);
    }, 0);
  };

  // Filter and group products
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      const matchesSearch = product.ItemNameEN.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.ItemNameFR.toLowerCase().includes(searchTerm.toLowerCase()) ||
                           product.ItemCode.includes(searchTerm);
      const matchesCategory = selectedCategory === 'all' || 
                             product.ItemSubCategoryEN === selectedCategory ||
                             product.ItemCategoryEN === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [products, searchTerm, selectedCategory]);

  const groupedProducts = useMemo(() => {
    return filteredProducts.reduce((acc, product) => {
      const key = `$${product.Price.toFixed(2)} ${product.ItemSubCategoryEN}`;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(product);
      return acc;
    }, {} as { [key: string]: Product[] });
  }, [filteredProducts]);

  // Memoize subcategory options to prevent re-renders
  const subCategoryOptions = useMemo(() => {
    const subCategories = Array.from(new Set(products.map(p => p.ItemSubCategoryEN))).sort();
    return subCategories.map(subCat => (
      <option key={subCat} value={subCat}>{subCat}</option>
    ));
  }, [products]);

  // Memoize language check to avoid recalculating on every render
  const isFrench = useMemo(() => i18n.language?.startsWith('fr') || false, [i18n.language]);

  // Memoize selected items calculations to prevent recalculation on every render
  const { selectedItemsCount, totalAmount } = useMemo(() => {
    const count = Object.values(selectedItems).reduce((sum, qty) => sum + qty, 0);
    const total = Object.entries(selectedItems).reduce((total, [productId, quantity]) => {
      const product = products.find(p => p.ItemCode === productId);
      return total + (product?.Price || 0) * quantity;
  }, 0);
    return { selectedItemsCount: count, totalAmount: total };
  }, [selectedItems, products]);

  // Calculate global totals for all forms
  // NOTE: This is memoized for performance but should NOT trigger updates
  // The GlobalOrderSummary component will read from orderData directly
  const globalTotals = React.useMemo(() => {
    const allItems = [
      ...(orderData.heritageItems || []),
      ...(orderData.livingstonItems || []),
      ...(orderData.flowerItems || []),
      ...(orderData.vegetableItems || [])
    ];
    
    const totalItems = allItems.reduce((sum, item) => sum + item.quantity, 0);
    const totalAmount = allItems.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    
    return { totalItems, totalAmount };
    // Use stable array length references instead of full orderData object
    // GlobalOrderSummary reads from orderData directly via useGlobalOrderData
  }, [
    orderData.heritageItems?.length,
    orderData.livingstonItems?.length,
    orderData.flowerItems?.length,
    orderData.vegetableItems?.length
  ]);

  return (
    <OptimizedFormContainer>
      {/* Form Status Indicator */}
      <FormStatusIndicator
        formType="vegetable"
        status={formStatus}
        onReset={clearDraft}
      />
      
      {/* Product Selection */}
      <OptimizedFormSection>
          <SectionTitle 
            icon={Carrot} 
            title={t('forms.vegetable.title')} 
            subtitle={t('forms.vegetable.subtitle')}
          />
          
          {/* Search and Filter */}
          <SearchAndFilterContainer>
            <SearchInput>
              <Search className="search-icon" size={20} />
              <input
                className="search-field"
                type="text"
                placeholder={t('forms.searchVegetablesPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </SearchInput>
            
            <FilterSelect>
              <select
                className="filter-field"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">{t('forms.allCategories', 'All Categories')}</option>
                {subCategoryOptions}
              </select>
            </FilterSelect>
          </SearchAndFilterContainer>

          {/* Loading State */}
          {productsLoading && (
            <div style={{ textAlign: 'center', padding: Spacing.xl }}>
              <LoadingSpinner />
              <p>{t('forms.loadingProducts')}</p>
            </div>
          )}

          {/* Error State */}
          {productsError && (
            <ErrorMessage>
              <AlertCircle size={20} />
              {productsError}
            </ErrorMessage>
          )}

          {/* Product Categories */}
          {!productsLoading && !productsError && Object.entries(groupedProducts).map(([categoryKey, categoryProducts]) => {
            const sectionId = `category-${categoryKey}`;
            const isCollapsed = collapsedSections[sectionId];
            
            return (
              <ProductCategorySection key={categoryKey}>
                <CategoryHeader>
                  <div className="category-info">
                    <div className="category-title">
                      {categoryKey}
                    </div>
                    <div className="category-count">
                      {categoryProducts.length} {categoryProducts.length === 1 ? t('common.item') : t('common.items')}
                    </div>
                  </div>
                  <div className="category-price">
                    ${categoryProducts[0].Price.toFixed(2)} {t('forms.each')}
                  </div>
                </CategoryHeader>
                
                <OptimizedProductGrid>
                  {categoryProducts.map((product) => {
                    const quantity = selectedItems[product.ItemCode] || 0;
                    const isSelected = quantity > 0;
                    const productName = isFrench ? product.ItemNameFR : product.ItemNameEN;
                    const productPrice = product.Price.toFixed(2);
                    
                    return (
                      <ProductCardMemo
                        key={product.ItemCode}
                        productCode={product.ItemCode}
                        productName={productName}
                        productPrice={productPrice}
                        quantity={quantity}
                        isSelected={isSelected}
                        category={product.ItemForm.toLowerCase()}
                        onQuantityChange={handleQuantityChange}
                        codeLabel={t('pdf.code')}
                      />
                    );
                  })}
                </OptimizedProductGrid>
              </ProductCategorySection>
            );
          })}

          {!productsLoading && !productsError && Object.keys(groupedProducts).length === 0 && (
            <div style={{ textAlign: 'center', padding: Spacing.xl }}>
              <p>No products found matching your search criteria.</p>
            </div>
          )}

          {errors.items && (
            <ErrorMessage>
              <AlertCircle size={20} />
              {errors.items}
            </ErrorMessage>
          )}
        </OptimizedFormSection>

        {/* Global Order Summary */}
        <GlobalOrderSummary />

        {/* Preview Order Button with Validation */}
        <PreviewOrderButton 
                onPreview={() => {
                  if (onShowPreview) {
                    onShowPreview();
                  } else {
                    console.log('Preview functionality not available');
                  }
                }}
              />
        
        {/* Back to Top Button */}
        <BackToTopButton scrollThreshold={300} />
    </OptimizedFormContainer>
  );
};
